import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, Chip, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Divider, TextField
} from '@mui/material';
import {
  Download as DownloadIcon, UploadFile as UploadIcon,
  CheckCircle as ApplyIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../../config/api';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Bulk Active/Inactive management (Rishabh 2026-07-06) — validation-first,
 * modeled on CIN7/NetSuite import safety:
 *   1. Download the brand's current status sheet (CSV).
 *   2. Edit the Status column in Excel, upload the file back.
 *   3. The server runs a DRY RUN and shows exactly what would change
 *      ("48 → Inactive, 12 → Active, 3 errors skipped") — nothing is written.
 *   4. Explicit Apply, with a second confirmation when deactivations exceed
 *      the server threshold. Identity columns (sku/internal_id/brand) are
 *      protected — edits to them turn into row errors, never writes.
 */
// reloadError (DT-2009): the parent's page error. After Apply this dialog stays open and onApplied reloads the
// parent's table; a failed reload sets the parent's page error, which renders behind this modal. Shown here only
// once applied, so an older page error never shows inside the dialog (the parent clears it when the reload starts).
export default function BulkStatusUpdateDialog({ open, onClose, brand, onApplied, reloadError = null }) {
  const [rows, setRows] = useState(null);          // parsed sheet rows
  const [fileName, setFileName] = useState('');
  const [validation, setValidation] = useState(null); // server dry-run result
  const [confirmText, setConfirmText] = useState(''); // type-the-count confirm
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);    // apply result summary

  const reset = () => {
    setRows(null); setFileName(''); setValidation(null);
    setConfirmText(''); setBusy(false); setError(null); setApplied(null);
  };
  const handleClose = () => { if (!busy) { reset(); onClose?.(); } };

  const downloadSheet = async () => {
    try {
      setError(null);
      const res = await fetch(`/api/cogs/brands/${brand.id}/products/status-sheet.csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
      const blob = await res.blob();
      let filename = `product-status-${brand.name || brand.id}.csv`;
      const cd = res.headers.get('content-disposition');
      const m = cd && cd.match(/filename="?([^"]+)"?/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to download status sheet');
    }
  };

  // Normalize header names so "Internal ID", "internal_id", "Status " all map.
  // __row carries the ORIGINAL spreadsheet row number (1-based + header) so
  // server error reports point at the line the user actually sees in Excel,
  // even after empty rows are dropped client-side (QA catch 2026-07-06).
  const normalizeRow = (o, idx) => {
    const out = { __row: idx + 2 };
    for (const [k, v] of Object.entries(o)) {
      const key = String(k).trim().toLowerCase().replace(/\s+/g, '_');
      if (['internal_id', 'id', 'product_id'].includes(key)) out.internal_id = v;
      else if (key === 'sku' || key === 'product_sku' || key === 'product_sku_code') out.sku = v;
      else if (key === 'status' || key === 'current_status' || key === 'new_status') out.status = v;
    }
    return out;
  };

  const parseFile = useCallback((file) => {
    setError(null); setValidation(null); setApplied(null); setConfirmText('');
    const ext = file.name.split('.').pop().toLowerCase();
    const finish = (objects) => {
      const normalized = objects.map(normalizeRow).filter(r => r.sku || r.internal_id);
      if (!normalized.length) {
        setError('No usable rows found — the file needs sku (or internal_id) and status columns.');
        return;
      }
      setRows(normalized);
      setFileName(file.name);
      runValidation(normalized);
    };
    const reader = new FileReader();
    if (ext === 'csv') {
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'string' });
          finish(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' }));
        } catch (err) { setError(`Could not parse ${file.name}: ${err.message}`); }
      };
      reader.readAsText(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          finish(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' }));
        } catch (err) { setError(`Could not parse ${file.name}: ${err.message}`); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError('Unsupported file type. Use .csv, .xlsx, or .xls');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id]);

  const runValidation = async (parsedRows) => {
    try {
      setBusy(true); setError(null);
      const { data } = await api.post(`/api/cogs/brands/${brand.id}/products/status-import/validate`, { rows: parsedRows });
      setValidation(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Validation failed'));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    try {
      setBusy(true); setError(null);
      const { data } = await api.post(`/api/cogs/brands/${brand.id}/products/status-import/apply`, {
        rows, validation_token: validation.validation_token, confirm_large: confirmOk
      });
      setApplied(data);
      onApplied?.(data);
    } catch (e) {
      const code = e.response?.data?.code;
      if (code === 'stale_validation') {
        setError('Product data changed since the preview — re-upload the file to validate again.');
        setValidation(null);
      } else {
        setError(apiErrorMessage(e, 'Apply failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadErrorReport = () => {
    const errs = validation?.errors || [];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = ['row,sku,internal_id,reason', ...errs.map(e => [e.row, e.sku, e.internal_id, e.reason].map(esc).join(','))].join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `status-import-errors-${brand.name || brand.id}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const s = validation?.summary;
  // Large deactivations require TYPING the exact count — a checkbox can be
  // ticked without reading; typing "87" cannot (QA catch 2026-07-06).
  const confirmOk = !validation?.requires_large_confirm
    || confirmText.trim() === String(s?.to_deactivate ?? '');
  const canApply = validation && !applied && (s.to_deactivate + s.to_activate) > 0 && confirmOk;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bulk status update — {brand?.name}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Download the sheet, change the <b>status</b> column in Excel, upload it back.
          Nothing is written until you review the preview and click Apply.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Step 1 + 2: download / upload */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadSheet} disabled={busy}>
            Download status sheet
          </Button>
          <Button startIcon={<UploadIcon />} variant="outlined" component="label" disabled={busy}>
            Upload edited sheet
            <input hidden type="file" accept=".csv,.xlsx,.xls"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); e.target.value = ''; }} />
          </Button>
          {fileName && <Typography variant="caption" color="text.secondary">{fileName} · {rows?.length} rows</Typography>}
          {busy && <CircularProgress size={18} />}
        </Stack>

        {/* Step 3: validation preview */}
        {validation && !applied && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Preview — nothing has been changed yet</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip color={s.to_deactivate > 0 ? 'error' : 'default'} label={`${s.to_deactivate} will change Active → Inactive`} />
              <Chip color={s.to_activate > 0 ? 'success' : 'default'} label={`${s.to_activate} will change Inactive → Active`} />
              <Chip variant="outlined" label={`${s.unchanged} unchanged — ignored`} />
              <Chip
                color={s.errors > 0 ? 'warning' : 'default'} variant={s.errors > 0 ? 'filled' : 'outlined'}
                label={`${s.errors} row${s.errors === 1 ? '' : 's'} with errors — skipped`}
                onClick={s.errors > 0 ? downloadErrorReport : undefined}
              />
              {(s.deactivating_used_in_boms || 0) > 0 && (
                <Chip color="warning" label={`⚠ ${s.deactivating_used_in_boms} being deactivated are still used in active recipes`} />
              )}
            </Stack>
            {s.errors > 0 && (
              <Button size="small" onClick={downloadErrorReport} sx={{ mb: 1 }}>Download error report</Button>
            )}
            {(s.deactivating_used_in_boms || 0) > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {s.deactivating_used_in_boms} of the products being deactivated are still components
                in active recipes (marked in the table below). Deactivating does NOT remove them from
                those recipes — future builds will still consume them.
              </Alert>
            )}
            {(validation.changes || []).length > 0 && (
              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validation.changes.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.sku}</TableCell>
                        <TableCell>
                          {c.product_name}
                          {(c.used_in_boms || 0) > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              ⚠ still used in {c.used_in_boms} active recipe line{c.used_in_boms > 1 ? 's' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{c.from}</TableCell>
                        <TableCell>
                          <Chip size="small" label={c.to}
                            color={c.to === 'Inactive' ? 'error' : 'success'}
                            variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {/* Type-the-count confirm sits DIRECTLY under the change table so
                confirming happens in the same visual context as the rows. */}
            {validation.requires_large_confirm && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This import deactivates <b>{s.to_deactivate}</b> products — more than the safety
                threshold of {validation.deactivation_confirm_threshold}. Review the list above,
                then type <b>{s.to_deactivate}</b> to confirm.
                <TextField
                  size="small" sx={{ display: 'block', mt: 1 }}
                  placeholder={String(s.to_deactivate)}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  inputProps={{ 'aria-label': 'confirmation count' }}
                />
              </Alert>
            )}
            {(s.to_deactivate + s.to_activate) === 0 && (
              <Alert severity="info">No status changes found — every row matches the current data.</Alert>
            )}
          </Box>
        )}

        {/* Step 4: applied summary */}
        {applied && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Applied: {applied.summary.to_deactivate} deactivated, {applied.summary.to_activate} activated,{' '}
            {applied.summary.unchanged} unchanged, {applied.summary.errors} skipped. All changes are in the audit log.
          </Alert>
        )}
        {applied && reloadError && <Alert severity="error" sx={{ mt: 1 }}>{reloadError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>{applied ? 'Close' : 'Cancel'}</Button>
        {!applied && (
          <Button variant="contained" startIcon={<ApplyIcon />} onClick={apply} disabled={!canApply || busy}>
            {busy ? 'Working…' : `Apply ${validation ? (s.to_deactivate + s.to_activate) : ''} change${validation && (s.to_deactivate + s.to_activate) === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
